/**
 * "Email me this report" button + recipient modal.
 *
 * Pass `reportType` and a `getFilters()` callback that returns the current
 * filter state to POST to `/api/reports/email`. The backend generates a PDF
 * attachment + inline HTML summary and dispatches via SMTP.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, X, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';

const EmailReportButton = ({ reportType, getFilters, label = 'Email me this report' }) => {
  const { authHeader, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState(user?.email || '');
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState('');

  const handleSend = async () => {
    const r = (recipient || '').trim().toLowerCase();
    if (!r || !r.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setSending(true);
    try {
      const filters = (typeof getFilters === 'function' && getFilters()) || {};
      const { data } = await axios.post(
        `${API}/reports/email`,
        { report_type: reportType, filters, recipient: r },
        { headers: authHeader() },
      );
      setSentTo(data?.recipient || r);
      toast.success(`Report sent to ${data?.recipient || r}`);
      setTimeout(() => { setOpen(false); setSentTo(''); }, 1800);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => { setRecipient(user?.email || ''); setOpen(true); }}
        className="text-sm bg-teal-50 hover:bg-teal-100 text-teal-800 border border-teal-200 font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5 transition"
        data-testid={`email-report-btn-${reportType}`}
        title="Send this report as a PDF to your inbox"
      >
        <Mail size={14} /> {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !sending && setOpen(false)}
          data-testid="email-report-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !sending && setOpen(false)}
              className="absolute top-3 right-3 p-1 hover:bg-slate-100 rounded"
              data-testid="email-report-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-teal-100 text-teal-700 rounded-lg">
                <Mail size={18} />
              </div>
              <div>
                <h2 className="font-bold text-slate-900">Email this report</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  We&apos;ll attach a PDF + include an inline summary.
                </p>
              </div>
            </div>

            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">
              Send to
            </label>
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className={inputCls}
              placeholder="you@unisol.global"
              disabled={sending}
              data-testid="email-report-recipient"
            />

            {sentTo ? (
              <div className="mt-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg p-3 flex items-center gap-2">
                <CheckCircle2 size={16} /> Sent to <b>{sentTo}</b>
              </div>
            ) : (
              <div className="mt-4 flex gap-2 justify-end">
                <button
                  onClick={() => setOpen(false)}
                  disabled={sending}
                  className="text-sm px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending}
                  className="text-sm bg-teal-600 hover:bg-teal-700 text-white font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="email-report-send"
                >
                  {sending ? (<><Loader2 className="animate-spin" size={14} /> Sending…</>) :
                    (<><Send size={14} /> Send report</>)}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EmailReportButton;
